import { memo } from "react";

import { RichText } from "@shopify/hydrogen-react";

import { Button } from "@components/Button";
import { Heading } from "@components/Heading";
import { Link } from "@components/Link";
import { Text } from "@components/Text";
import { focusRing } from "@react/utilities";
import { type CollectionContentCard } from "@root/tablescape/state";
import { tw } from "classnames";

type Props = Pick<
  CollectionContentCard,
  "title" | "description" | "background" | "ctaText" | "ctaUrl"
>;

const styles = {
  base: tw(
    "block",
    "h-60",
    "overflow-hidden",
    "before:float-left",
    "before:-ml-1",
    "before:h-0",
    "before:w-1",
    "before:pt-full",
    "after:clear-both",
    "after:table",
    "after:content-['']",
    "md:h-auto",
    "md:before:content-['']",
    "no-underline",
    focusRing
  ),
  content: ({ background }: { background: string }) =>
    tw(
      "flex",
      "h-full",
      "w-full",
      "flex-col",
      "justify-center",
      "items-start",
      "p-6",
      "md:p-10",
      {
        [tw("bg-background-secondary", "text-primary")]: background === "bone",
        [tw("bg-sage-700", "text-white")]: background === "sage",
        [tw("bg-char-900", "text-white")]: background === "char",
        [tw("bg-interactive-brick", "text-white")]: background === "brick",
      }
    ),
  title: tw("mb-2", "md:mb-4"),
  description: tw("mb-6"),
  button: tw("px-7", "whitespace-nowrap"),
};

export const CollectionTextCardViewBase = ({
  background = "bone",
  ctaText,
  ctaUrl,
  description,
  title,
}: Props) => {
  const Tag = ctaUrl ? Link : "div";
  return (
    <Tag
      className={styles.base}
      {...(ctaUrl
        ? { to: ctaUrl, unstyled: true, mtrTarget: "cta" }
        : { to: "" })}
    >
      <div className={styles.content({ background })}>
        {title && (
          <RichText
            className={styles.title}
            components={{
              paragraph({ node }) {
                return (
                  <Heading as="h3" size="sm">
                    {node.children}
                  </Heading>
                );
              },
            }}
            data={title}
          />
        )}
        {description && (
          <RichText
            className={styles.description}
            components={{
              paragraph({ node }) {
                return <Text as="body1">{node.children}</Text>;
              },
            }}
            data={description}
          />
        )}
        {ctaUrl && ctaText && (
          <Button
            className={styles.button}
            mtrTrack={false}
            onClick={() => {}}
            type={background === "bone" ? "secondary" : "secondary-alt"}
          >
            {ctaText}
          </Button>
        )}
      </div>
    </Tag>
  );
};

export const CollectionTextCard = memo(CollectionTextCardViewBase);
